
import Vue from "vue";
import { Swiper, SwiperSlide } from "vue-awesome-swiper";
import "swiper/css/swiper.css";
import CatIcon from "@/components/atoms/CatIcon.vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import type { AssistantCat, AssistantCatGroup, AssistantCatMaster } from "@/lib/models/assistantCat";
import { Plan } from "@/lib/models";
import { mapGetters } from "vuex";
import { NolaAnalytics, NolaItemId } from "@/lib/utils/analytics";

// アイコンのインポート
const laIcon = require("@/assets/img/assistant-cat/la-icon.png");
const lpIcon = require("@/assets/img/assistant-cat/lp-icon.png");
const maIcon = require("@/assets/img/assistant-cat/ma-icon.png");
const mpIcon = require("@/assets/img/assistant-cat/mp-icon.png");

// 定数定義（型安全性と再利用性の向上）
const GROUP_ORDER: AssistantCatGroup[] = ["MP", "LA", "LP", "MA"];
const TYPE_ORDER: Record<AssistantCatGroup, string[]> = {
  MP: ["EMPH", "EMPS", "IMPH", "IMPS"],
  LA: ["ELAH", "ELAS", "ILAH", "ILAS"],
  LP: ["ELPH", "ELPS", "ILPH", "ILPS"],
  MA: ["EMAH", "EMAS", "IMAH", "IMAS"],
};

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    Swiper,
    SwiperSlide,
    CatIcon,
    LoadingSpinner,
  },
  props: {
    assistantCatMaster: {
      type: Object as () => AssistantCatMaster | null,
      default: null,
    },
  },
  data() {
    return {
      assistantCats: [] as AssistantCat[],
      isLoading: false,
      selectedGroup: null as AssistantCatGroup | null,
      currentPage: 0,
      isMobile: false,
      error: null as string | null,
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
    };
  },
  computed: {
    ...mapGetters("billingModule", ["hasPaymentError"]),
    swiperOptions() {
      return {
        loop: true,
        centeredSlides: true,
        slidesPerView: this.isMobile ? 1.5 : 2, // モバイル: 1.5枚、PC: 2枚
        spaceBetween: this.isMobile ? 20 : 0, // モバイル: 20px、PC: 0px
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        speed: 500,
        mousewheel: true,
      };
    },
    groupOptions() {
      return [
        { code: "MP" as AssistantCatGroup, name: this.$t("myPage.assistantCatDialog.groups.mp") as string },
        { code: "LA" as AssistantCatGroup, name: this.$t("myPage.assistantCatDialog.groups.la") as string },
        { code: "LP" as AssistantCatGroup, name: this.$t("myPage.assistantCatDialog.groups.lp") as string },
        { code: "MA" as AssistantCatGroup, name: this.$t("myPage.assistantCatDialog.groups.ma") as string },
      ];
    },
    plan() {
      return this.$store.getters["billingModule/plan"];
    },
    isFreePlan() {
      return this.plan === Plan.free;
    },
    writerTypeCat() {
      if (!this.assistantCatMaster?.writerType) {
        return null;
      }
      return (
        this.assistantCats.find((cat: AssistantCat) => cat.assistantCatType === this.assistantCatMaster.writerType) ||
        null
      );
    },
    filteredCats() {
      // データ読み込み中は空配列を返す
      if (this.assistantCats.length === 0) {
        return [];
      }

      let cats: AssistantCat[] = [];
      if (!this.selectedGroup) {
        cats = this.getOrderedCats();
      } else {
        // グループフィルタリング時もソート
        const filteredCats = this.assistantCats.filter((cat: AssistantCat) => cat.group === this.selectedGroup);
        cats = this.orderCatsByType(filteredCats);
      }

      return cats;
    },
  },
  watch: {
    selectedGroup() {
      // グループ変更時は先頭ページに移動
      this.currentPage = 0;

      // Swiperのスライド位置を先頭にリセット（loopモード対応）
      this.$nextTick(() => {
        const swiper = (this.$refs.catSwiper as { $swiper?: { slideToLoop: (index: number, speed: number) => void } })
          ?.$swiper;
        if (swiper) {
          swiper.slideToLoop(0, 0); // loopモードで0番目にアニメーションなしで移動
        }
      });
    },
  },
  methods: {
    getGroupIcon(group: AssistantCatGroup): string {
      const iconMap = {
        LA: laIcon,
        LP: lpIcon,
        MA: maIcon,
        MP: mpIcon,
      };
      return iconMap[group];
    },
    handleGroupSelect(group: AssistantCatGroup) {
      if (this.selectedGroup === group) {
        // 選択中のタブを押下するとフィルタリングを解除し、初期状態に戻す
        this.selectedGroup = null;
        this.currentPage = 0; // 初期表示位置に戻す
      } else {
        this.selectedGroup = group;
        this.currentPage = 0; // グループ選択時も先頭に移動
      }
    },
    async handleSelectCat(cat: AssistantCat) {
      try {
        this.isLoading = true;

        // APIを呼び出して選択状態を更新・Vuexストアに選択状態をキャッシュ
        await this.$store.dispatch("assistantCatModule/updateSelectedCat", {
          selectedType: cat.assistantCatType,
        });

        // サーバーから最新データを取得して状態を同期
        await this.fetchAssistantCats();

        // GAイベントを発火
        this.nolaAnalytics.logButtonEvent(NolaItemId.AssistantCatChangeSelectionType, cat.assistantCatType);

        this.$notify({
          title: (this.$t("myPage.assistantCatDialog.successMessage") as string),
          type: "success",
        });

        // ダイアログを閉じる
        this.handleClose();
      } catch (error) {
        console.error("Failed to change assistant cat:", error);
        this.$notify({
          title: (this.$t("myPage.assistantCatDialog.errorMessage") as string),
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    handleClose() {
      this.$emit("close", true);
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
    },
    async fetchAssistantCats() {
      this.isLoading = true;
      this.error = null;

      try {
        this.assistantCats = await this.$store.dispatch("assistantCatModule/fetchAssistantCatList");

        // データ読み込み後に選択中の猫の位置を設定
        setTimeout(() => {
          this.currentPage = 0;
        }, 100);
      } catch (error) {
        console.error("Failed to fetch assistant cats:", error);
        this.error = (this.$t("myPage.assistantCatDialog.loadErrorMessage") as string);
      } finally {
        this.isLoading = false;
      }
    },
    handleRetry() {
      this.fetchAssistantCats();
    },
    shouldShowPremiumPrompt(cat: AssistantCat) {
      return this.isFreePlan && !cat.selected && !this.isWriterTypeCat(cat) && !cat.isSynergyType && !cat.isImpactType;
    },
    onClickUpgrade() {
      this.$router.push({ name: "subscriptionAnnounce" });
    },
    isWriterTypeCat(cat: AssistantCat): boolean {
      if (!this.assistantCatMaster?.writerType) {
        return false;
      }
      return cat.assistantCatType === this.assistantCatMaster.writerType;
    },
    getOrderedCats() {
      const cats = [...this.assistantCats];

      // 各タイプの猫を分類（選択中の猫を除く）
      const synergyCat = cats.find((cat) => cat.isSynergyType)!;
      const writerTypeCat = cats.find((cat) => this.isWriterTypeCat(cat))!;
      const impactCat = cats.find((cat) => cat.isImpactType)!;
      // その他の猫を取得（選択中の猫を除く）
      const otherCats = cats.filter((cat) => !cat.isSynergyType && !this.isWriterTypeCat(cat) && !cat.isImpactType)!;

      // その他の猫をグループ別に並び替え
      const orderedOtherCats = this.orderOtherCatsByGroup(otherCats);

      // 並び順を決定（選択中の猫のタイプに応じて動的に変更）
      let orderedCats: AssistantCat[] = [synergyCat, writerTypeCat, impactCat, ...orderedOtherCats];

      // 選択中の猫の位置を先頭として配列をローテーション
      const selectedCatIndex = orderedCats.findIndex((cat) => cat.selected);
      if (selectedCatIndex !== -1 && selectedCatIndex > 0) {
        const beforeSelected = orderedCats.slice(0, selectedCatIndex);
        const fromSelected = orderedCats.slice(selectedCatIndex);
        orderedCats = [...fromSelected, ...beforeSelected];
      }

      return orderedCats;
    },
    orderCatsByType(cats: AssistantCat[]) {
      // グループ内のタイプ順序でソート（TYPE_ORDERの定義通り）
      return this.orderOtherCatsByGroup(cats);
    },
    orderOtherCatsByGroup(otherCats: AssistantCat[]) {
      // Map を使用して O(1) でアクセス（パフォーマンス最適化）
      const catMap = new Map<string, AssistantCat>();
      otherCats.forEach((cat) => {
        catMap.set(cat.assistantCatType, cat);
      });

      const orderedCats: AssistantCat[] = [];

      // 定数を使用してグループ順に処理
      GROUP_ORDER.forEach((group) => {
        const groupTypeOrder = TYPE_ORDER[group];

        // グループ内のタイプ順に並び替え
        groupTypeOrder.forEach((type) => {
          const cat = catMap.get(type);
          if (cat) {
            orderedCats.push(cat);
          }
        });
      });

      return orderedCats;
    },
    /**
     * vue-awesome-swiperの内部動作で初期表示→左に移動した場合に変更するボタンが効かない問題を解消
     *
     * 【原因】
     * Swiperのloop modeでは、スライドのDOM要素が複製される
     * - 実スライド: Vueがレンダリングした元のDOM（@clickイベントハンドラ付き）
     * - duplicateスライド: Swiperが自動生成した複製DOM（イベントハンドラがコピーされない）
     *
     * 左に移動すると配列の最後のスライドの複製（duplicate）が表示されますが、この複製には
     * Vueの@click="handleSelectCat(cat)"が存在しないため、ボタンをクリックしても何も起こらない
     *
     * 【解決方法】
     * イベント委譲パターンを使用
     * 1. Swiperコンテナ全体でクリックイベントをキャッチ
     * 2. クリックされた要素がボタンかチェック
     * 3. スライドのdata-swiper-slide-index属性から実際のデータインデックスを取得
     * 4. filteredCats[index]で対応する猫データを取得して処理を実行
     */
    handleSwiperClick(event: MouseEvent) {
      // クリックされた要素がボタンかどうかをチェック
      const target = event.target as HTMLElement;
      const button = target.closest(".action-button--active, .premium-register-button");

      if (!button) {
        return; // ボタン以外のクリックは無視
      }

      // クリックされたスライドを取得
      const slide = target.closest(".swiper-slide") as HTMLElement;
      if (!slide) {
        return;
      }

      // data-swiper-slide-index属性から実際のインデックスを取得
      const slideIndex = slide.getAttribute("data-swiper-slide-index");
      if (slideIndex === null) {
        return;
      }

      // インデックスに対応するcatデータを取得
      const cat = this.filteredCats[parseInt(slideIndex, 10)];
      if (!cat) {
        return;
      }

      // ボタンの種類に応じて処理を実行
      if (button.classList.contains("premium-register-button")) {
        this.onClickUpgrade();
      } else if (button.classList.contains("action-button--active")) {
        // プレミアムプロンプトが表示されている場合は変更ボタンのクリックを無視
        if (this.shouldShowPremiumPrompt(cat)) {
          return;
        }
        this.handleSelectCat(cat);
      }
    },
  },
  async mounted() {
    // bodyのスクロールを無効化
    document.body.style.overflow = "hidden";

    // 画面サイズの初期チェック
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);

    // アシスタント猫データを取得
    await this.fetchAssistantCats();

    // Swiperのduplicateスライド内のボタンクリックを処理するためのイベント委譲
    this.$nextTick(() => {
      const swiperContainer = this.$refs.catSwiper?.$el;
      if (swiperContainer) {
        swiperContainer.addEventListener("click", this.handleSwiperClick);
      }
    });
  },
  beforeDestroy() {
    // bodyのスクロールを元に戻す
    document.body.style.overflow = "";

    // Swiperコンテナのイベントリスナーを削除
    const swiperContainer = this.$refs.catSwiper?.$el;
    if (swiperContainer) {
      swiperContainer.removeEventListener("click", this.handleSwiperClick);
    }

    // イベントリスナーを削除
    window.removeEventListener("resize", this.checkScreenSize);
  },
});

interface Props {
  assistantCatMaster: AssistantCatMaster | null;
}

interface Data {
  assistantCats: AssistantCat[];
  isLoading: boolean;
  selectedGroup: AssistantCatGroup | null;
  currentPage: number;
  isMobile: boolean;
  error: string | null;
  nolaAnalytics: NolaAnalytics;
}

interface Computed {
  groupOptions: Array<{ code: AssistantCatGroup; name: string }>;
  plan: Plan;
  isFreePlan: boolean;
  writerTypeCat: AssistantCat | null;
  filteredCats: AssistantCat[];
  hasPaymentError: boolean;
  swiperOptions: {
    loop: boolean;
    centeredSlides: boolean;
    slidesPerView: number;
    spaceBetween: number;
    navigation: {
      nextEl: string;
      prevEl: string;
    };
    speed: number;
    mousewheel: boolean;
  };
}

interface Methods {
  getGroupIcon: (group: AssistantCatGroup) => string;
  handleGroupSelect: (group: AssistantCatGroup) => void;
  handleSelectCat: (cat: AssistantCat) => Promise<void>;
  handleClose: () => void;
  checkScreenSize: () => void;
  fetchAssistantCats: () => Promise<void>;
  handleRetry: () => void;
  shouldShowPremiumPrompt: (cat: AssistantCat) => boolean;
  onClickUpgrade: () => void;
  isWriterTypeCat: (cat: AssistantCat) => boolean;
  getOrderedCats: () => AssistantCat[];
  orderCatsByType: (cats: AssistantCat[]) => AssistantCat[];
  orderOtherCatsByGroup: (otherCats: AssistantCat[]) => AssistantCat[];
  handleSwiperClick: (event: MouseEvent) => void;
}
